import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownItemContent,
  IconButton,
} from '@ads/components-react';
import {
  ArrowDownTrayIcon,
  DocumentIcon,
  EllipsisHorizontalIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDownloadPersonAttachment } from '@/features/student-management/hooks/useDownloadPersonAttachment';
import { usePersonAttachments } from '@/features/student-management/hooks/usePersonAttachments';
import type { DocumentationStatus } from '@/features/student-management/types/enrollment';
import type { PersonAttachmentItemViewModel } from '@/shared/api/generated/types';
import { Spinner } from '@/shared/ui/atoms';
import { DocumentationStatusBadge } from '../../../atoms/DocumentationStatusBadge';
import {
  DocumentProgressRing,
  DocumentRejectedAlert,
  SectionCard,
  SectionTitle,
} from '../../../molecules/DocumentationSectionParts';
import type { UploadAttachmentSubmitData } from '../../UploadAttachmentModal/UploadAttachmentModal';
import { UploadAttachmentModal } from '../../UploadAttachmentModal/UploadAttachmentModal';

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface DocumentsTabProps {
  readonly personId: number | undefined;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_MAP: Record<0 | 1 | 2 | 3, DocumentationStatus> = {
  0: 'pending',
  1: 'approved',
  2: 'rejected',
  3: 'approved_temporary',
};

function toDocumentationStatus(
  raw: PersonAttachmentItemViewModel['documentationStatus'],
): DocumentationStatus {
  if (raw === 0 || raw === 1 || raw === 2 || raw === 3) return STATUS_MAP[raw];
  return 'pending';
}

function buildSubtitle(
  attachment: PersonAttachmentItemViewModel,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const status = toDocumentationStatus(attachment.documentationStatus);
  const date = attachment.creationDate ? dayjs(attachment.creationDate).format('DD/MM') : '';

  const parts: string[] = [];

  if (status === 'approved') {
    if (date) parts.push(t('enrollmentDocumentation.approvedAt', { date }));
  } else if (status === 'approved_temporary') {
    if (date) parts.push(t('enrollmentDocumentation.approvedTempAt', { date }));
  } else if (status === 'rejected') {
    if (date) parts.push(t('enrollmentDocumentation.rejectedAt', { date }));
    if (attachment.reasonName) parts.push(attachment.reasonName);
  } else {
    if (date) parts.push(t('enrollmentDocumentation.pendingAt', { date }));
  }

  return parts.join(' · ');
}

// ─── Sub-componente local ─────────────────────────────────────────────────────

function DocumentRow({
  attachment,
  personId,
  downloadingId,
  onDownload,
  onDelete,
}: {
  attachment: PersonAttachmentItemViewModel;
  personId: number;
  downloadingId: number | null;
  onDownload: (id: number, fileName: string | null | undefined) => void;
  onDelete: (id: number) => void;
}) {
  const { t } = useTranslation('personManagement');
  const status = toDocumentationStatus(attachment.documentationStatus);
  const subtitle = buildSubtitle(
    attachment,
    t as (key: string, opts?: Record<string, unknown>) => string,
  );
  const attachmentId = attachment.id ?? 0;

  return (
    <div className="flex items-center gap-xxs border-b-hairline border-ctx-layout-border py-xxxs last:border-b-0">
      <div className="flex h-[2rem] w-[2rem] shrink-0 items-center justify-center rounded-xs bg-ctx-layout-surface">
        <DocumentIcon className="h-xxxs w-xxxs text-ctx-content-placeholder" />
      </div>

      <div className="flex min-w-none flex-1 flex-col gap-quark">
        <span className="ts-paragraph-xs !font-medium text-ctx-content-title">
          {attachment.documentTypeName ?? attachment.fileName ?? '—'}
        </span>
        {subtitle && (
          <span className="ts-caption-xxs text-ctx-content-placeholder truncate">{subtitle}</span>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-quark">
        <DocumentationStatusBadge status={status} />
        <IconButton
          icon={ArrowDownTrayIcon}
          hierarchy="tertiary"
          size="sm"
          ariaLabel={t('actions.download')}
          isLoading={downloadingId === attachmentId}
          disabled={!personId}
          onClick={() => onDownload(attachmentId, attachment.fileName)}
        />
        <Dropdown
          trigger={
            <IconButton
              icon={EllipsisHorizontalIcon}
              hierarchy="tertiary"
              size="sm"
              ariaLabel={t('actions.moreOptions')}
            />
          }
          align="end"
        >
          <DropdownItem status="destructive" onSelect={() => onDelete(attachmentId)}>
            <DropdownItemContent leadingIcon={TrashIcon}>{t('actions.delete')}</DropdownItemContent>
          </DropdownItem>
        </Dropdown>
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

/**
 * Tab de upload e listagem de documentos anexados à pessoa.
 *
 * @param personId - ID da pessoa. Quando `undefined` (modo criação), a tab fica desabilitada.
 */
export function DocumentsTab({ personId }: DocumentsTabProps) {
  const { t } = useTranslation('personManagement');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const { attachments, isLoading, upload, remove, isUploading } = usePersonAttachments(personId);

  const { download, downloadingId } = useDownloadPersonAttachment(personId ?? 0);

  const handleUploadSubmit = (data: UploadAttachmentSubmitData) => {
    upload(
      data.file,
      {
        ...(data.classStudentId !== undefined ? { classStudentId: data.classStudentId } : {}),
        ...(data.enrollmentDocumentId !== undefined
          ? { enrollmentDocumentId: data.enrollmentDocumentId }
          : {}),
      },
      { onSuccess: () => setIsUploadModalOpen(false) },
    );
  };

  const approvedCount = attachments.filter(a => {
    const s = toDocumentationStatus(a.documentationStatus);
    return s === 'approved' || s === 'approved_temporary';
  }).length;
  const pendingCount = attachments.filter(
    a => toDocumentationStatus(a.documentationStatus) === 'pending',
  ).length;
  const rejectedCount = attachments.filter(
    a => toDocumentationStatus(a.documentationStatus) === 'rejected',
  ).length;
  const total = attachments.length;

  const rejectedWithReason = attachments.filter(
    a => toDocumentationStatus(a.documentationStatus) === 'rejected' && !!a.reasonName,
  );

  return (
    <div className="flex flex-col gap-xxs bg-ctx-layout-surface">
      {/* Progresso + alertas de rejeição */}
      {total > 0 && (
        <div className="grid grid-cols-1 gap-xxs md:grid-cols-2">
          <div className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs">
            <DocumentProgressRing
              approved={approvedCount}
              total={total}
              pending={pendingCount}
              rejected={rejectedCount}
            />
          </div>
          {rejectedWithReason.length > 0 && (
            <div className="flex flex-col gap-quark">
              {rejectedWithReason.map(a => (
                <DocumentRejectedAlert
                  key={a.id}
                  docType={a.documentTypeName ?? a.fileName ?? ''}
                  reason={a.reasonName ?? ''}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Lista de documentos */}
      <SectionCard
        header={
          <SectionTitle
            icon={DocumentIcon}
            title={t('enrollmentDocumentation.sectionTitle')}
            {...(total > 0
              ? {
                  description: t('enrollmentDocumentation.sectionDescription', {
                    count: total,
                  }),
                }
              : {})}
            action={
              <Button
                hierarchy="primary"
                size="sm"
                leadingIcon={PlusIcon}
                disabled={!personId}
                onClick={() => setIsUploadModalOpen(true)}
              >
                {t('enrollmentDocumentation.attachNew')}
              </Button>
            }
          />
        }
      >
        <div className="flex flex-col">
          {isLoading ? (
            <div className="flex justify-center py-xs">
              <Spinner />
            </div>
          ) : attachments.length === 0 ? (
            <p className="ts-paragraph-xs text-ctx-content-placeholder py-nano">
              {t('table.empty')}
            </p>
          ) : (
            attachments.map(attachment => (
              <DocumentRow
                key={attachment.id}
                attachment={attachment}
                personId={personId ?? 0}
                downloadingId={downloadingId}
                onDownload={(id, fileName) => void download(id, fileName ?? undefined)}
                onDelete={id => remove(id)}
              />
            ))
          )}
        </div>
      </SectionCard>

      {personId !== undefined && (
        <UploadAttachmentModal
          personId={personId}
          open={isUploadModalOpen}
          isLoading={isUploading}
          onClose={() => setIsUploadModalOpen(false)}
          onSubmit={handleUploadSubmit}
        />
      )}
    </div>
  );
}
